import { call, all, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import {
  requestCreateInventoryStockLine,
  createInventoryStockLineSuccess,
} from 'redux/modules/inventoryStockLine';
import { addNotification } from 'redux/modules/notifications';
import { saveRecoverOrder } from 'redux/modules/recoverOrder';

// Actions
export const FETCH_INVENTORIES = 'FETCH_INVENTORIES';
export const FETCH_INVENTORIES_SUCCESS = 'FETCH_INVENTORIES_SUCCESS';
export const FETCH_INVENTORIES_FAIL = 'FETCH_INVENTORIES_FAIL';
export const CLEAR_INVENTORIES = 'CLEAR_INVENTORIES';
export const FETCH_INVENTORY = 'FETCH_INVENTORY';
export const FETCH_INVENTORY_SUCCESS = 'FETCH_INVENTORY_SUCCESS';
export const FETCH_INVENTORY_FAIL = 'FETCH_INVENTORY_FAIL';
export const CREATE_INVENTORY = 'CREATE_INVENTORY';
export const CREATE_INVENTORY_SUCCESS = 'CREATE_INVENTORY_SUCCESS';
export const CREATE_INVENTORY_FAIL = 'CREATE_INVENTORY_FAIL';
export const UPDATE_INVENTORY = 'UPDATE_INVENTORY';
export const UPDATE_INVENTORY_SUCCESS = 'UPDATE_INVENTORY_SUCCESS';
export const UPDATE_INVENTORY_FAIL = 'UPDATE_INVENTORY_FAIL';
export const DELETE_INVENTORY = 'DELETE_INVENTORY';
export const DELETE_INVENTORY_SUCCESS = 'DELETE_INVENTORY_SUCCESS';
export const DELETE_INVENTORY_FAIL = 'DELETE_INVENTORY_FAIL';

// Reducer
export default function inventoryReducer(state = { data: [], count: 0, loading: 0 }, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_INVENTORIES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_INVENTORIES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INVENTORIES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_INVENTORIES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case FETCH_INVENTORY:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case FETCH_INVENTORY_SUCCESS: {
      let isPresent = false;
      let newCount = state.count;
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        isPresent = true;
        return {
          ...dataItem,
          ...payload,
        };
      });
      if (!isPresent) {
        newCount += 1;
        newData.push(payload);
      }
      return {
        ...state,
        count: newCount,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INVENTORY_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case UPDATE_INVENTORY_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_INVENTORY_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchInventories(payload) {
  return { type: FETCH_INVENTORIES, payload };
}

export function fetchInventoriesSuccess(payload) {
  return { type: FETCH_INVENTORIES_SUCCESS, payload };
}

export function fetchInventoriesFail(payload) {
  return { type: FETCH_INVENTORIES_FAIL, payload };
}

export function fetchInventory(payload) {
  return { type: FETCH_INVENTORY, payload };
}

export function fetchInventorySuccess(payload) {
  return { type: FETCH_INVENTORY_SUCCESS, payload };
}

export function fetchInventoryFail(payload) {
  return { type: FETCH_INVENTORY_FAIL, payload };
}

export function clearInventories() {
  return { type: CLEAR_INVENTORIES };
}

export function createInventory(payload) {
  return { type: CREATE_INVENTORY, payload };
}

export function createInventorySuccess(payload) {
  return { type: CREATE_INVENTORY_SUCCESS, payload };
}

export function createInventoryFail(payload) {
  return { type: CREATE_INVENTORY_FAIL, payload };
}

export function updateInventory(payload) {
  return { type: UPDATE_INVENTORY, payload };
}

export function updateInventorySuccess(payload) {
  return { type: UPDATE_INVENTORY_SUCCESS, payload };
}

export function updateInventoryFail(payload) {
  return { type: UPDATE_INVENTORY_FAIL, payload };
}

export function deleteInventory(payload) {
  return { type: DELETE_INVENTORY, payload };
}

export function deleteInventorySuccess(payload) {
  return { type: DELETE_INVENTORY_SUCCESS, payload };
}

export function deleteInventoryFail(payload) {
  return { type: DELETE_INVENTORY_FAIL, payload };
}

// Requests

let abort;

export function requestFetchInventories({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi(`inventory/main/?limit=${limit}&offset=${offset}`);
  abort = request.abort;
  return request.res;
}

export function requestSearchInventories({ limit, offset, filters = [], sortBy = [] }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('inventory/main/search/', 'POST', {
    filters,
    sort_by: [
      ...sortBy,
      ...sortBy.filter(item => item.name === 'ID').length === 0 ? [{
        name: 'ID',
        sort: 'desc',
      }] : [],
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestFetchInventory(payload) {
  return callApi(`inventory/main/?id=${payload}`);
}

export function requestCreateInventory(payload) {
  return callApi('inventory/main/', 'POST', payload);
}

export function requestUpdateInventory(payload) {
  return callApi('inventory/main/', 'PATCH', payload);
}

export function requestDeleteInventory(payload) {
  return callApi(`inventory/main/?id=${payload}`, 'DELETE');
}

export function requestAddInventoryResource(payload) {
  return callApi('inventory/main/change_resource/add/', 'POST', payload);
}

export function requestCreateInventoryDocument(payload) {
  return callApi('inventory/document/', 'POST', payload, false);
}

// Sagas
function* fetchInventoriesSaga(action) {
  const {
    triggerLoading,
    filters,
    sortBy,
    recoverOrderValues,
  } = action.payload;
  try {
    const res = filters || sortBy
      ? yield call(requestSearchInventories, action.payload)
      : yield call(requestFetchInventories, action.payload);
    if (res.count === 0) {
      yield put(fetchInventoriesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else if (action.payload.push) {
      yield put(fetchInventoriesSuccess({
        push: true,
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else {
      yield put(fetchInventoriesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    if (err.data.error === 'Access Error') {
      yield put(saveRecoverOrder(recoverOrderValues));
    }
    yield put(fetchInventoriesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* fetchInventorySaga(action) {
  const { triggerLoading, ID } = action.payload;
  try {
    const res = yield call(requestFetchInventory, ID);
    if (res.data.ID) {
      yield put(fetchInventorySuccess({
        ...res.data,
        triggerLoading,
      }));
      yield put(fetchAccountInformationSuccess(res.user_info));
      yield put(fetchAppInfoSuccess(res.app_info));
    } else {
      yield put(fetchInventoryFail({
        triggerLoading,
      }));
    }
  } catch (err) {
    yield put(fetchInventoryFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createInventorySaga(action) {
  const {
    values,
    values: {
      Quantity,
      Price,
      Image,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    delete otherValues['Quantity In Case'];
    delete otherValues['Facility ID'];
    delete otherValues['Warehouse ID'];
    delete otherValues['Expiration Date'];
    delete otherValues['Retail Price']
    delete otherValues['Is Retail Price For Claims'];
    delete otherValues['Is Retail Price For Self Pay'];
    const res = yield call(requestCreateInventory, {
      ...otherValues,
      ...Image && Image[0] && Image[0].ID ? {
        'Image ID': Image.map(item => item.ID),
      } : {},
    });
    yield put(createInventorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (Image && !Image[0].ID) {
      const imageRes = yield all(
        Image.map((item) => {
          const currentImage = item;
          const formData = new FormData();
          formData.append('object_id', res.data.ID);
          if (currentImage.noFileConstructor) {
            delete currentImage.noFileConstructor;
            formData.append('file', currentImage, currentImage.name);
          } else {
            formData.append('file', currentImage);
          }
          return call(requestCreateInventoryDocument, formData);
        }),
      );
      yield call(requestUpdateInventory, { ID: res.data.ID, 'Image ID': imageRes.map(item => item.data.ID) });
      yield put(addNotification({
        message: 'New product successfully added',
        variant: 'success',
      }));
    } else {
      yield put(addNotification({
        message: 'New product successfully added',
        variant: 'success',
      }));
    }
    if (Quantity && Price) {
      const stockLineRes = yield call(requestCreateInventoryStockLine, {
        'Product ID': res.data.ID,
        'Hcpc Code ID': res.data['Main Hcpc Code'].ID,
        Quantity,
        Price,
        ...values['Facility ID'] ? {
          'Facility ID': values['Facility ID'],
        } : {},
        ...values['Warehouse ID'] ? {
          'Warehouse ID': values['Warehouse ID'],
        } : {},
        ...values['Expiration Date'] ? {
          'Expiration Date': values['Expiration Date'],
        } : {},
        ...values['Retail Price'] ? {
          'Retail Price': values['Retail Price'],
        } : {},
        ...values['Is Retail Price For Claims'] ? {
          'Is Retail Price For Claims': values['Is Retail Price For Claims'],
        } : {},
        ...values['Is Retail Price For Self Pay'] ? {
          'Is Retail Price For Self Pay': values['Is Retail Price For Self Pay'],
        } : {},
        ...values['Quantity In Case'] ? {
          'Quantity In Case': values['Quantity In Case'],
        } : {},
      });
      yield put(createInventoryStockLineSuccess({
        'Product ID': res.data.ID,
        'Stock Line': stockLineRes.data,
      }));
      yield put(addNotification({
        message: 'Products successfully added to inventory',
        variant: 'success',
      }));
      if (resolve) {
        resolve({
          ...res.data,
          ...res.data['Stock Line'] ? {
            'Stock Line': [
              ...res.data['Stock Line'],
              stockLineRes.data,
            ],
          } : {},
          'Last Arrival Stock Line': {
            ...stockLineRes.data,
          },
        });
      }
    } else if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createInventoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateInventorySaga(action) {
  const {
    values: {
      ID,
      Quantity,
      Price,
      Image,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    let res;
    if (Object.keys(otherValues).length > 0) {
      res = yield call(requestUpdateInventory, {
        ID,
        ...otherValues,
      });
      yield put(updateInventorySuccess(res.data));
      yield put(fetchAccountInformationSuccess(res.user_info));
      yield put(fetchAppInfoSuccess(res.app_info));
    }
    if (Image) {
      const newImages = Image.filter(item => !item.ID);
      const existingImageIDs = Image.filter(item => item.ID).map(item => item.ID);
      const newImagesRes = yield all(
        newImages.map((item) => {
          const currentImage = item;
          const formData = new FormData();
          formData.append('object_id', ID);
          if (currentImage.noFileConstructor) {
            delete currentImage.noFileConstructor;
            formData.append('file', currentImage, currentImage.name);
          } else {
            formData.append('file', currentImage);
          }
          return call(requestCreateInventoryDocument, formData);
        }),
      );
      const inventory = yield call(requestUpdateInventory, {
        ID,
        'Image ID': [
          ...existingImageIDs,
          ...newImagesRes.map(item => item.data.ID),
        ],
      });
      yield put(updateInventorySuccess(inventory.data));
      yield put(fetchAccountInformationSuccess(inventory.user_info));
      yield put(fetchAppInfoSuccess(inventory.app_info));
      yield put(addNotification({
        message: 'Product was successfully updated',
        variant: 'success',
      }));
      if (resolve) {
        resolve(inventory.data);
      }
    } else {
      yield put(addNotification({
        message: 'Product was successfully updated',
        variant: 'success',
      }));
      if (resolve) {
        resolve(res.data);
      }
    }
  } catch (err) {
    yield put(updateInventoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteInventorySaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteInventory, ID);
    yield put(deleteInventorySuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Product was successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteInventoryFail(err.data));
    yield put(addNotification({
      message: err.data.error || 'Failed to delete product',
      variant: 'warning',
    }));
  }
}

export function* inventorySaga() {
  yield takeLatest(FETCH_INVENTORIES, fetchInventoriesSaga);
  yield takeEvery(FETCH_INVENTORY, fetchInventorySaga);
  yield takeEvery(CREATE_INVENTORY, createInventorySaga);
  yield takeEvery(UPDATE_INVENTORY, updateInventorySaga);
  yield takeEvery(DELETE_INVENTORY, deleteInventorySaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/inventory.js